import { getBlocklyWorkspace, getScratchBlocks } from "../blocks";
import { targetToName, nameToTarget, stageName } from "./scratch-helpers";
import { onSpriteAdd, onSpriteDelete, onSpriteRename } from "../sprites";
import {
    onCostumeAdded,
    onCostumeDelete,
    onCostumeReorder,
    onCostumeRename,
    onCostumeDuplicate,
    onSvgUpdated,
    onBitmapUpdate,
} from "../sprites/costumes";
import { onBackdropAdded } from "../sprites/backdrops";
import {
    onSoundAdded,
    onSoundDelete,
    onSoundReorder,
    onSoundRename,
    onSoundDuplicate,
    onSoundUpdate,
} from "../sprites/sounds";

const CREATE_FLY_MS = 500;
const MOVE_ANIM_MS = 320;
const MIN_ANIM_MS = 90;
const OFFSCREEN_MARGIN_PX = 60;

export class ReplayEventAdapter {
    constructor(vm) {
        this.vm = vm;
        this.workspace = getBlocklyWorkspace();
        this._activeTarget = this._currentTargetName();
        this._activeCategory = null;
        this._snapshotBaseline();
    }

    _snapshotBaseline() {
        this._baselineTargetNames = new Set();
        this._baselineAssets = new Map();

        for (const target of this.vm.runtime.targets) {
            const name = targetToName(target);
            if (!name) continue;

            this._baselineTargetNames.add(name);
            this._baselineAssets.set(name, {
                costumes: new Set(
                    (target.getCostumes?.() || []).map((c) => c.assetId),
                ),
                sounds: new Set(
                    (target.sprite?.sounds || []).map((s) => s.assetId),
                ),
            });
        }
    }

    resetToBaseline() {
        for (const target of [...this.vm.runtime.targets]) {
            if (target.isStage) continue;

            const name = targetToName(target);
            if (name && !this._baselineTargetNames.has(name)) {
                try {
                    this.vm.deleteSprite(target.id);
                } catch (e) {
                    console.error(
                        "[Replay] Error removing a sprite added during replay:",
                        e,
                    );
                }
            }
        }

        for (const target of this.vm.runtime.targets) {
            const original = this._baselineAssets.get(targetToName(target));
            if (!original) continue;

            this.vm.setEditingTarget(target.id);

            const costumes = target.getCostumes?.() || [];
            for (let i = costumes.length - 1; i >= 0; i--) {
                if (original.costumes.has(costumes[i].assetId)) continue;
                try {
                    this.vm.deleteCostume(i);
                } catch (e) {
                    console.error(
                        "[Replay] Error removing a costume added during replay:",
                        e,
                    );
                }
            }

            const sounds = target.sprite?.sounds || [];
            for (let i = sounds.length - 1; i >= 0; i--) {
                if (original.sounds.has(sounds[i].assetId)) continue;
                try {
                    this.vm.deleteSound(i);
                } catch (e) {
                    console.error(
                        "[Replay] Error removing a sound added during replay:",
                        e,
                    );
                }
            }
        }

        this._activeTarget = this._currentTargetName();
        this._activeCategory = null;
    }

    adapt(evt) {
        switch (evt.eventType) {
            case "create":
                return this._create(evt);
            case "move":
                return this._move(evt);
            case "delete":
                return this._delete(evt);
            case "change":
                return this._change(evt);

            case "sprite_added":
                return this._spriteAdded(evt);
            case "sprite_deleted":
                return this._spriteDeleted(evt);
            case "sprite_renamed":
                return this._spriteRenamed(evt);

            case "costume_added":
                return this._followedAction(onCostumeAdded, evt);
            case "costume_reordered":
                return this._followedAction(onCostumeReorder, evt);
            case "costume_renamed":
                return this._followedAction(onCostumeRename, evt);
            case "costume_duplicated":
                return this._followedAction(onCostumeDuplicate, evt);
            case "costume_deleted":
                return this._followedAction(onCostumeDelete, evt);
            case "svg_updated":
                return this._followedAction(onSvgUpdated, evt);
            case "bitmap_updated":
                return this._followedAction(onBitmapUpdate, evt);

            case "backdrop_added":
                return this._backdropAdded(evt);

            case "sound_added":
                return this._followedAction(onSoundAdded, evt);
            case "sound_reordered":
                return this._followedAction(onSoundReorder, evt);
            case "sound_renamed":
                return this._followedAction(onSoundRename, evt);
            case "sound_duplicated":
                return this._followedAction(onSoundDuplicate, evt);
            case "sound_updated":
                return this._followedAction(onSoundUpdate, evt);
            case "sound_deleted":
                return this._followedAction(onSoundDelete, evt);

            default:
                return null;
        }
    }

    _create(evt) {
        const { blockId, blockType, target, json } = evt.data;

        return async (animate = true, rate = 1) => {
            this._switchToTargetName(target);

            const ws = getBlocklyWorkspace();
            if (ws.getBlockById(blockId)) return;

            this._showCategoryOf(blockType, ws);

            let block;
            if (json) {
                this._runBlocklyEvent(json, ws);
                block = ws.getBlockById(blockId);
            } else {
                const xml = this._findToolboxBlockXml(blockType, blockId);
                if (xml) {
                    const dom = getScratchBlocks().Xml.textToDom(
                        `<xml>${xml}</xml>`,
                    );
                    getScratchBlocks().Xml.domToWorkspace(dom, ws);
                    block = ws.getBlockById(blockId);
                } else {
                    block = ws.newBlock(blockType, blockId);
                    block.initSvg();
                    block.render();
                }
            }

            if (!animate || !block || block.getParent()) return;

            const landing = block.getRelativeToSurfaceXY();
            const startX = this._getOffscreenLeftX(ws) ?? landing.x - 600;
            block.moveBy(startX - landing.x, 0);

            await this._animateBlockTo(
                block,
                landing.x,
                landing.y,
                CREATE_FLY_MS / rate,
            );
        };
    }

    _move(evt) {
        const { blockId, newX, newY, target, json } = evt.data;

        return async (animate = true, rate = 1) => {
            this._switchToTargetName(target);

            const ws = getBlocklyWorkspace();
            const block = ws.getBlockById(blockId);
            if (!block) return;

            if (!block.getParent()) {
                if (animate) {
                    await this._animateBlockTo(
                        block,
                        newX,
                        newY,
                        MOVE_ANIM_MS / rate,
                    );
                } else {
                    const pos = block.getRelativeToSurfaceXY();
                    block.moveBy(newX - pos.x, newY - pos.y);
                }
            }

            this._runBlocklyEvent(json, ws);
        };
    }

    _change(evt) {
        const { target, json } = evt.data;

        return () => {
            this._switchToTargetName(target);
            this._runBlocklyEvent(json, getBlocklyWorkspace());
        };
    }

    _runBlocklyEvent(json, ws) {
        if (!json) return;

        try {
            const event = getScratchBlocks().Events.fromJson(json, ws);
            event.run(true);
        } catch (e) {
            console.error("[Replay] Error applying block event:", e);
        }
    }

    _animateBlockTo(block, toX, toY, durationMs) {
        return new Promise((resolve) => {
            const start = block.getRelativeToSurfaceXY();
            const dx = toX - start.x;
            const dy = toY - start.y;
            if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
                resolve();
                return;
            }

            const duration = Math.max(MIN_ANIM_MS, durationMs);
            const ease = (t) => 1 - Math.pow(1 - t, 3);
            const startTime = performance.now();
            let lastX = 0;
            let lastY = 0;

            const step = (now) => {
                if (!block.workspace) {
                    resolve();
                    return;
                }

                const t = Math.min(1, (now - startTime) / duration);
                const e = ease(t);
                const curX = dx * e;
                const curY = dy * e;
                block.moveBy(curX - lastX, curY - lastY);
                lastX = curX;
                lastY = curY;

                if (t < 1) requestAnimationFrame(step);
                else resolve();
            };

            requestAnimationFrame(step);
        });
    }

    _getOffscreenLeftX(ws) {
        try {
            const parentSvg = ws.getParentSvg?.();
            const canvas = ws.getCanvas?.();
            const ctm = canvas?.getScreenCTM?.();
            if (!parentSvg || !ctm) return null;

            const rect = parentSvg.getBoundingClientRect();
            const pt = parentSvg.createSVGPoint();
            pt.x = rect.left - OFFSCREEN_MARGIN_PX;
            pt.y = rect.top + rect.height / 2;

            return pt.matrixTransform(ctm.inverse()).x;
        } catch (e) {
            return null;
        }
    }

    _delete(evt) {
        const blockId = evt.data.blId || evt.data.blockId;
        const { target } = evt.data;

        return () => {
            this._switchToTargetName(target);

            const ws = getBlocklyWorkspace();
            const block = ws.getBlockById(blockId);
            if (block) block.dispose(false);
        };
    }

    _followedAction(handler, evt) {
        return async () => {
            await handler({ data: evt.data });
            if (evt.data.target) this._switchToTargetName(evt.data.target);
        };
    }

    _spriteAdded(evt) {
        return async () => {
            await onSpriteAdd({ data: evt.data });
            try {
                const info = JSON.parse(evt.data.info);
                if (info?.name) this._switchToTargetName(info.name);
            } catch (e) {
                console.error("[Replay] Error reading added sprite name:", e);
            }
        };
    }

    _spriteDeleted(evt) {
        return async () => {
            await onSpriteDelete({ data: evt.data });
            this._activeTarget = this._currentTargetName();
        };
    }

    _spriteRenamed(evt) {
        return () => {
            onSpriteRename({ data: evt.data });
            if (this._activeTarget === evt.data.oldName) {
                this._activeTarget = evt.data.newName;
            }
        };
    }

    _backdropAdded(evt) {
        return async () => {
            await onBackdropAdded({ data: evt.data });
            this._switchToTargetName(stageName);
        };
    }

    _showCategoryOf(blockType, ws) {
        const toolbox = ws.toolbox_;
        if (!toolbox?.setSelectedCategoryById) return;

        const categoryId = this._findCategoryId(
            ws.options.languageTree,
            blockType,
            null,
        );
        if (!categoryId || categoryId === this._activeCategory) return;

        try {
            toolbox.setSelectedCategoryById(categoryId);
            this._activeCategory = categoryId;
        } catch (e) {
            console.error("[Replay] Error selecting block category:", e);
        }
    }

    _findCategoryId(node, blockType, categoryId) {
        if (!node) return null;

        if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.tagName === "category")
                categoryId = node.getAttribute("id") || categoryId;

            if (
                node.tagName === "block" &&
                node.getAttribute("type") === blockType
            )
                return categoryId;
        }

        for (let i = 0; i < node.childNodes.length; i++) {
            const found = this._findCategoryId(
                node.childNodes[i],
                blockType,
                categoryId,
            );
            if (found) return found;
        }

        return null;
    }

    _findToolboxBlockXml(blockType, blockId) {
        try {
            const ws = getBlocklyWorkspace();
            const toolbox = ws.options.languageTree;
            if (!toolbox) {
                console.warn("Toolbox not found", toolbox);
                return null;
            }

            const template = this._searchXmlNode(toolbox, blockType);
            if (!template) {
                console.warn("No template found", template);
                return null;
            }

            const clone = template.cloneNode(true);
            clone.setAttribute("id", blockId);
            return getScratchBlocks().Xml.domToText(clone);
        } catch (e) {
            return null;
        }
    }

    _searchXmlNode(node, blockType) {
        if (
            node.nodeType === Node.ELEMENT_NODE &&
            node.tagName === "block" &&
            node.getAttribute("type") === blockType
        ) {
            return node;
        }
        for (let i = 0; i < node.childNodes.length; i++) {
            const found = this._searchXmlNode(node.childNodes[i], blockType);
            if (found) return found;
        }

        return null;
    }

    _switchToTargetName(targetName) {
        if (!targetName || targetName === this._activeTarget) return;

        const target = nameToTarget(targetName);
        if (!target) return;

        this.vm.setEditingTarget(target.id);
        this._activeTarget = targetName;
    }

    _currentTargetName() {
        const t = this.vm.editingTarget;
        return t ? targetToName(t) : stageName;
    }
}
